/**
 * AuthService - JWT-Based Authentication
 *
 * This service handles authentication using PKCE OAuth2 flow with ZITADEL.
 * It integrates with the backend proxy for token management.
 */

import {
  registerGlobalTokenProvider,
  tokenStore,
  unregisterGlobalTokenProvider,
} from '@peritasai/data-access';
import { LoginResponse, User, UserProfile } from '../../../shared/auth';
import { env } from '../../../shared/utils/env';
import { sanitizeUserForMemory } from '../../../shared/utils/security';
import { pkceService } from './pkce.service';
import {
  sessionService,
  type LogoutResult,
} from './session.service';

/**
 * Authentication Service
 *
 * This service provides methods for user authentication and user profile operations
 * using PKCE OAuth2 flow with ZITADEL and session-based authentication.
 */
export class AuthService {
  // In-memory user data storage
  private static currentUser: User | null = null;
  private static currentUserProfile: UserProfile | null = null;

  private static setCurrentSession(
    user: User,
    userProfile: UserProfile
  ): void {
    const shouldPreserveSessionMetadata =
      this.currentUser?.id === user.id && this.currentUser.id !== '';
    const inactivityLimitMs = shouldPreserveSessionMetadata
      ? this.currentUser?.inactivityLimitMs ?? user.inactivityLimitMs
      : user.inactivityLimitMs;
    const sessionKey = shouldPreserveSessionMetadata
      ? this.currentUser?.sessionKey ?? user.sessionKey
      : user.sessionKey;

    this.currentUser = {
      ...user,
      inactivityLimitMs,
      sessionKey,
    };
    this.currentUserProfile = {
      ...userProfile,
      inactivityLimitMs,
      sessionKey,
    };
  }

  /**
   * Initialize the authentication service
   * This should be called when the application starts
   */
  static async initialize(): Promise<void> {
    // Register token refresh fn for authenticatedFetch 401 retry
    tokenStore.setRefreshFn(() => sessionService.refreshAccessToken());

    // Notify app when token refreshes so React Query can invalidate and refetch (Module Federation safe)
    tokenStore.setOnRefreshSuccess(() => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('periverse:token-refreshed'));
      }
    });

    // Register global token provider so Module Federation remotes (peri-tray, peri-track)
    // can obtain the token when they have their own bundled copy of data-access-shared
    registerGlobalTokenProvider();

    // Derive session from JWT tokens (no /me call)
    const session = await sessionService.getSessionFromTokens();

    if (session) {
      this.setCurrentSession(session.user, session.userProfile);
    } else {
      // Clear user data if no valid session
      this.currentUser = null;
      this.currentUserProfile = null;
    }
  }

  /**
   * Start PKCE login flow
   *
   * This initiates the PKCE OAuth2 flow by redirecting to ZITADEL.
   * The actual login happens after the user returns from ZITADEL.
   */
  static async login(targetWindow: Window = window): Promise<LoginResponse> {
    // For PKCE flow, we don't validate credentials here
    // Instead, we redirect to ZITADEL for authentication

    // Get authorization URL from PKCE service (backend will generate PKCE parameters)
    const frontendRedirectUri = targetWindow?.location?.origin
      ? `${targetWindow.location.origin}/auth/callback`
      : env.REDIRECT_URI; // Fallback if needed, though window exists in CSR

    const authUrl = await pkceService.getAuthorizationUrl(frontendRedirectUri);

    // Store redirect URI and return URL for callback
    if (targetWindow?.location) {
      sessionStorage.setItem(
        'pkce_redirect_uri',
        `${targetWindow.location.origin}/auth/callback`
      );
      // Preserve an existing return URL (set by _auth beforeLoad when session expired mid-navigation)
      // so the user lands back on the page they were on, not the root.
      if (!sessionStorage.getItem('pkce_return_url')) {
        const returnUrl =
          targetWindow.location.pathname === '/' ? '/' : targetWindow.location.pathname;
        sessionStorage.setItem('pkce_return_url', returnUrl);
      }

      targetWindow.location.replace(authUrl);
    }

    // This will never be reached due to redirect, but we need to satisfy the return type
    // In a real implementation, the user will be redirected back to /auth/callback
    // and the handlePKCECallback method will be called
    throw new Error('Redirecting to ZITADEL for authentication');
  }

  /**
   * Handle PKCE callback after ZITADEL redirect
   */
  static async handlePKCECallback(
    code: string,
    codeVerifier: string,
    redirectUri: string
  ): Promise<LoginResponse> {
    try {
      // Exchange authorization code for session
      const sessionData = await sessionService.exchangeCodeForSession(
        code,
        codeVerifier,
        redirectUri
      );

      // Validate required fields from backend response
      if (!sessionData.user) {
        throw new Error('Backend response missing user data');
      }

      // Store user data
      this.setCurrentSession(sessionData.user, sessionData.userProfile);

      // Return sanitized user data
      const sanitizedUser = sanitizeUserForMemory(
        sessionData.user as UserProfile
      );

      return {
        user: sanitizedUser,
      };
    } catch (error) {
      throw new Error(
        `PKCE callback failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  /**
   * Logout user.
   * Returns { redirected: true } when navigating to Zitadel - caller should not update state.
   * If redirect fails, onRedirectFailure runs full cleanup after timeout.
   */
  static async logout(): Promise<LogoutResult> {
    try {
      const result = await sessionService.logout({
        onRedirectFailure: () => {
          this.clearLocalAuthRuntime();
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('periverse:logout-redirect-failed'));
          }
        },
      });

      if (result.redirected) {
        return result;
      }

      this.clearLocalAuthRuntime();
      return { redirected: false };
    } catch (error) {
      this.clearLocalAuthRuntime();
      throw error;
    }
  }

  static clearLocalAuthRuntime(): void {
    tokenStore.clear();
    unregisterGlobalTokenProvider();
    this.currentUser = null;
    this.currentUserProfile = null;
  }

  /**
   * Get current user
   */
  static getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * Get current user profile
   */
  static getCurrentUserProfile(): UserProfile | null {
    return this.currentUserProfile;
  }

  /**
   * Get user profile
   */
  static async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      // Return cached profile if available
      if (this.currentUserProfile && this.currentUserProfile.id === userId) {
        return this.currentUserProfile;
      }

      // Derive session from JWT tokens
      const session = await sessionService.getSessionFromTokens();
      if (session?.userProfile) {
        this.setCurrentSession(session.user, session.userProfile);
        return this.currentUserProfile;
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Update user profile
   */
  static async updateUserProfile(
    userId: string,
    updates: Partial<UserProfile>
  ): Promise<UserProfile | null> {
    try {
      // Update cached profile
      if (this.currentUserProfile && this.currentUserProfile.id === userId) {
        this.currentUserProfile = { ...this.currentUserProfile, ...updates };
        return this.currentUserProfile;
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Validate session with backend
   */
  static async validateSession(): Promise<User | null> {
    try {
      // Derive session from JWT tokens
      const session = await sessionService.getSessionFromTokens();
      if (session?.user) {
        this.setCurrentSession(session.user, session.userProfile);
        return this.currentUser;
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Validate session with backend (on-demand)
   * This can be called when making API requests to ensure session is still valid
   */
  static async validateSessionWithBackend(): Promise<boolean> {
    try {
      const session = await sessionService.getSessionFromTokens();
      if (session) {
        this.setCurrentSession(session.user, session.userProfile);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }
}
